/**
 * DEVLOGS-TEMP — device/workspace identity, registered once and read at flush.
 *
 * Identity is not known at boot: a player has no deviceId until it has claimed a
 * screen credential and fetched /device/me. So the envelope source is a
 * registered getter, read at flush time, not a value captured at init.
 *
 * Unknown fields are OMITTED, never null — the same rule as
 * `playbackFailedPayload` in @workspace/player-shell. An absent key reads as
 * "this shell could not know", which is the truth.
 */

import type { DevLogEnvelope } from "./types.ts";

export type DevLogContext = Partial<
  Omit<DevLogEnvelope, "sentAt" | "seqFrom" | "seqTo" | "drops">
>;

export type DevLogContextSource = () => DevLogContext;

let source: DevLogContextSource | null = null;

/** Register (or clear) the identity getter. Never throws. */
export function registerDevLogContext(next: DevLogContextSource | null): void {
  source = next;
}

export function getDevLogContext(): DevLogContext {
  if (source === null) return {};
  try {
    const value = source();
    return value && typeof value === "object" ? value : {};
  } catch {
    return {};
  }
}

/** Drop undefined/empty values so the wire never carries `"deviceId": null`. */
export function compactContext(context: DevLogContext): DevLogContext {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(context)) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && value.length === 0) continue;
    out[key] = value;
  }
  return out as DevLogContext;
}
